<template>
  <b-modal
    :id="id"
    ref="modal"
    size="xl"
    hide-header
    hide-footer
    body-class="p-0 overflow-hidden"
    content-class="border-0 shadow-lg"
    dialog-class="modal-xl-custom"
    @shown="onShown"
  >
    <!-- ─── HEADER ─── -->
    <div class="report-header-premium d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 p-md-4 no-print">
      <div class="report-header-premium__info d-flex align-items-center mb-3 mb-md-0">
        <div class="report-header-premium__icon d-none d-sm-flex">
          <i class="fas fa-hand-holding-usd"></i>
        </div>
        <div class="ml-sm-3">
          <h5 class="report-header-premium__title">Análise Gerencial de Lucratividade</h5>
          <div class="d-flex align-items-center flex-wrap">
            <span class="report-header-premium__sub">Período: {{ mesExtenso }} de {{ filtro.ano }}</span>
            <b-badge variant="success" pill class="ml-2 py-1 px-2 border border-white-50 shadow-sm" style="font-size: 0.65rem;">
              <i class="fas fa-chart-line mr-1"></i> PERFORMANCE
            </b-badge>
          </div>
        </div>
      </div>
      
      <div class="d-flex align-items-center flex-wrap gap-2">
        <!-- Seletores de Período -->
        <div class="filter-pills d-flex align-items-center bg-white-10 rounded-pill px-2 py-1 mr-2">
          <b-form-select 
            v-model="filtro.mes" 
            :options="opcoesMeses" 
            size="sm" 
            class="bg-transparent border-0 text-white font-weight-bold" 
            style="width: 110px; cursor: pointer; color: white !important;"
            @change="buscarDados"
          ></b-form-select>
          <div class="v-divider mx-1"></div>
          <b-form-select 
            v-model="filtro.ano" 
            :options="opcoesAnos" 
            size="sm" 
            class="bg-transparent border-0 text-white font-weight-bold" 
            style="width: 70px; cursor: pointer; color: white !important;"
            @change="buscarDados"
          ></b-form-select>
        </div>

        <b-button variant="link" class="report-header-premium__close p-1" @click="fechar">
          <i class="fa fa-times text-white"></i>
        </b-button>
      </div>
    </div>

    <!-- ─── CORPO DO RELATÓRIO ─── -->
    <div class="report-container bg-soft-gray overflow-auto" style="max-height: 85vh;">
      
      <!-- Overlay de Carregamento -->
      <div v-if="loading" class="text-center p-5">
        <b-spinner variant="primary" label="Carregando..."></b-spinner>
        <p class="mt-2 text-muted">Processando dados financeiros...</p>
      </div>

      <template v-else>
        <!-- KPIs Rápidos -->
        <div class="p-3 p-md-4 pb-0">
          <b-row>
            <b-col sm="4" class="mb-3 mb-md-0">
              <div class="kpi-card-premium border-left-primary h-100 mb-0">
                <small>Faturamento Bruto</small>
                <div class="d-flex align-items-end justify-content-between">
                  <h4 class="mb-0 text-primary">{{ formatBRL(totais.bruto) }}</h4>
                  <div class="kpi-icon-bg bg-primary-light d-none d-lg-flex"><i class="fas fa-shopping-cart"></i></div>
                </div>
              </div>
            </b-col>
            <b-col sm="4" class="mb-3 mb-md-0">
              <div class="kpi-card-premium border-left-danger h-100 mb-0">
                <small>Custo Total Mercadorias</small>
                <div class="d-flex align-items-end justify-content-between">
                  <h4 class="mb-0 text-danger">{{ formatBRL(totais.custo) }}</h4>
                  <div class="kpi-icon-bg bg-danger-light d-none d-lg-flex"><i class="fas fa-tags"></i></div>
                </div>
              </div>
            </b-col>
            <b-col sm="4">
              <div class="kpi-card-premium border-left-success shadow-success h-100 mb-0">
                <small>Lucro Bruto Estimado</small>
                <div class="d-flex align-items-end justify-content-between">
                  <h4 class="mb-0 text-success">{{ formatBRL(totais.lucro) }}</h4>
                  <div class="kpi-icon-bg bg-success-light d-none d-lg-flex"><i class="fas fa-piggy-bank"></i></div>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Seção de Gráficos -->
        <div class="p-3 p-md-4">
          <b-row>
            <b-col md="12">
              <b-card class="border-0 shadow-sm mb-4 chart-card">
                <template #header>
                  <div class="d-flex align-items-center justify-content-between w-100">
                    <div class="d-flex align-items-center">
                      <i class="fas fa-chart-area mr-2 text-primary"></i>
                      <span class="font-weight-bold">Evolução do Lucro no Período</span>
                    </div>
                  </div>
                </template>
                <div class="chart-container" style="position: relative; height: 250px;">
                  <canvas ref="chartEvolucao"></canvas>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="6">
              <b-card class="border-0 shadow-sm mb-4 chart-card">
                <template #header>
                  <div class="d-flex align-items-center">
                    <i class="fas fa-chart-pie mr-2 text-success"></i>
                    <span class="font-weight-bold">Lucro por Grupo</span>
                  </div>
                </template>
                <div class="chart-container" style="position: relative; height: 280px;">
                  <canvas ref="chartGrupos"></canvas>
                </div>
              </b-card>
            </b-col>

            <b-col lg="6">
              <b-card class="border-0 shadow-sm mb-4 chart-card">
                <template #header>
                  <div class="d-flex align-items-center">
                    <i class="fas fa-box-open mr-2 text-info"></i>
                    <span class="font-weight-bold">Top Produtos</span>
                  </div>
                </template>
                <div class="chart-container" style="position: relative; height: 280px;">
                  <canvas ref="chartProdutos"></canvas>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="12">
              <b-card class="border-0 shadow-sm overflow-hidden">
                <template #header>
                  <div class="d-flex align-items-center justify-content-between flex-wrap">
                    <div class="d-flex align-items-center mb-2 mb-md-0">
                      <i class="fas fa-list-ul mr-2 text-dark"></i>
                      <span class="font-weight-bold">Detalhamento Analítico</span>
                    </div>
                    <b-input-group size="sm" style="width: 200px;">
                      <b-form-input v-model="searchTerm" placeholder="Filtrar..."></b-form-input>
                    </b-input-group>
                  </div>
                </template>
                <b-table
                  :items="dadosFiltrados"
                  :fields="fields"
                  hover
                  striped
                  responsive
                  class="mb-0"
                  head-variant="light"
                  show-empty
                  empty-text="Nenhum dado encontrado."
                >
                  <template #cell(DT_SAIDA)="data">
                    {{ formatDataBR(data.value) }}
                  </template>
                  <template #cell(VL_BRUTO)="data">
                    {{ formatBRL(data.value) }}
                  </template>
                  <template #cell(VL_LUCRO)="data">
                    <span class="text-success font-weight-bold">{{ formatBRL(data.value) }}</span>
                  </template>
                  <template #cell(MARGEM)="data">
                    <div class="d-flex align-items-center">
                      <div class="progress flex-grow-1 mr-2 d-none d-sm-flex" style="height: 6px;">
                        <div class="progress-bar" :style="{ width: calculateMargem(data.item) + '%', backgroundColor: getMargemHex(data.item) }"></div>
                      </div>
                      <span class="small font-weight-bold">{{ calculateMargem(data.item) }}%</span>
                    </div>
                  </template>
                </b-table>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </template>

    </div>

    <!-- ─── FOOTER ─── -->
    <div class="p-3 bg-white border-top d-flex justify-content-between align-items-center no-print">
      <div class="text-muted small d-none d-md-block pl-3">
        <i class="fas fa-info-circle mr-1"></i> Lucro = Venda - Custo Unitário.
      </div>
      <div class="w-100 d-flex justify-content-end">
        <b-button variant="outline-secondary" pill class="px-3 px-md-4 font-weight-bold mr-2 transition-all" @click="fechar">
          FECHAR
        </b-button>
        <b-button variant="primary" pill class="px-3 px-md-4 font-weight-bold shadow-sm transition-all" @click="imprimirAnalise()">
          <i class="fa fa-print mr-1"></i> IMPRIMIR ANÁLISE
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import Chart from 'chart.js'
import moment from 'moment'
import 'moment/locale/pt-br'

export default {
  name: 'LucroDashboardModal',
  props: {
    id: { type: String, default: 'modal-lucro-dashboard' }
  },
  data() {
    return {
      loading: false,
      dadosLocal: [],
      searchTerm: '',
      filtro: {
        mes: moment().month() + 1,
        ano: moment().year()
      },
      charts: {
        evolucao: null,
        grupos: null,
        produtos: null
      },
      opcoesMeses: [
        { value: 1, text: 'Janeiro' }, { value: 2, text: 'Fevereiro' },
        { value: 3, text: 'Março' }, { value: 4, text: 'Abril' },
        { value: 5, text: 'Maio' }, { value: 6, text: 'Junho' },
        { value: 7, text: 'Julho' }, { value: 8, text: 'Agosto' },
        { value: 9, text: 'Setembro' }, { value: 10, text: 'Outubro' },
        { value: 11, text: 'Novembro' }, { value: 12, text: 'Dezembro' }
      ],
      fields: [
        { key: 'DT_SAIDA', label: 'Data', sortable: true },
        { key: 'PRODUTO', label: 'Produto', sortable: true },
        { key: 'VL_BRUTO', label: 'Total Bruto', class: 'text-right' },
        { key: 'VL_LUCRO', label: 'Lucro', class: 'text-right' },
        { key: 'MARGEM', label: 'Margem %', class: 'text-center' }
      ]
    }
  },
  computed: {
    opcoesAnos() {
      const anos = []
      const anoAtual = moment().year()
      for (let i = 0; i < 5; i++) {
        anos.push({ value: anoAtual - i, text: (anoAtual - i).toString() })
      }
      return anos
    },
    mesExtenso() {
      const mes = this.opcoesMeses.find(m => m.value === this.filtro.mes)
      return mes ? mes.text : ''
    },
    totais() {
      const bruto = this.dadosLocal.reduce((acc, it) => acc + (it.VL_BRUTO || 0), 0)
      const custo = this.dadosLocal.reduce((acc, it) => acc + (it.VL_CUSTO || 0), 0)
      const lucro = this.dadosLocal.reduce((acc, it) => acc + (it.VL_LUCRO || 0), 0)
      return { bruto, custo, lucro }
    },
    resumoGrupos() {
      const gruposMap = {}
      this.dadosLocal.forEach(it => {
        const nome = it.GRUPO || 'DIVERSOS'
        if (!gruposMap[nome]) {
          gruposMap[nome] = { GRUPO: nome, VL_BRUTO: 0, VL_CUSTO: 0, VL_LUCRO: 0 }
        }
        gruposMap[nome].VL_BRUTO += (it.VL_BRUTO || 0)
        gruposMap[nome].VL_CUSTO += (it.VL_CUSTO || 0)
        gruposMap[nome].VL_LUCRO += (it.VL_LUCRO || 0)
      })
      return Object.values(gruposMap).sort((a, b) => b.VL_LUCRO - a.VL_LUCRO)
    },
    dadosFiltrados() {
      if (!this.searchTerm) return this.dadosLocal
      const term = this.searchTerm.toLowerCase()
      return this.dadosLocal.filter(it => 
        (it.PRODUTO || '').toLowerCase().includes(term) ||
        (it.VENDEDOR || '').toLowerCase().includes(term) ||
        (it.GRUPO || '').toLowerCase().includes(term)
      )
    }
  },
  methods: {
    formatBRL(v) {
      return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatDataBR(v) {
      if (!v) return '-'
      return moment(v).format('DD/MM/YYYY')
    },
    calculateMargem(item) {
      if (!item.VL_BRUTO || item.VL_BRUTO === 0) return 0
      return Math.round((item.VL_LUCRO / item.VL_BRUTO) * 100)
    },
    getMargemHex(item) {
      const m = (item.VL_LUCRO / item.VL_BRUTO) * 100
      if (m > 30) return '#10b981'
      if (m > 15) return '#3b82f6'
      if (m > 5) return '#f59e0b'
      return '#ef4444'
    },
    fechar() {
      this.$bvModal.hide(this.id)
    },
    onShown() {
      moment.locale('pt-br')
      this.buscarDados()
    },
    async buscarDados() {
      this.loading = true
      try {
        const response = await this.$http.get('api/protected/venda/detalharLucroMensal', {
          params: { mes: this.filtro.mes, ano: this.filtro.ano }
        })
        this.dadosLocal = response.data || []
        this.loading = false
        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (error) {
        console.error('Erro ao buscar dados de lucro:', error)
        this.loading = false
      }
    },
    renderCharts() {
      setTimeout(() => {
        this.renderEvolucao()
        this.renderGrupos()
        this.renderProdutos()
      }, 100)
    },
    renderEvolucao() {
      if (!this.$refs.chartEvolucao) return
      const ctx = this.$refs.chartEvolucao.getContext('2d')
      if (this.charts.evolucao) this.charts.evolucao.destroy()
      const diasMap = {}
      this.dadosLocal.forEach(it => {
        const dia = moment(it.DT_SAIDA).format('DD/MM')
        if (!diasMap[dia]) diasMap[dia] = 0
        diasMap[dia] += (it.VL_LUCRO || 0)
      })
      const labels = Object.keys(diasMap).sort()
      const values = labels.map(l => diasMap[l])
      this.charts.evolucao = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Lucro Diário',
            data: values,
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: true,
            tension: 0.3,
            pointBackgroundColor: '#fff',
            pointBorderColor: '#10b981',
            pointRadius: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{ ticks: { beginAtZero: true, callback: value => this.formatBRL(value) } }]
          }
        }
      })
    },
    renderGrupos() {
      if (!this.$refs.chartGrupos) return
      const ctx = this.$refs.chartGrupos.getContext('2d')
      if (this.charts.grupos) this.charts.grupos.destroy()
      const gruposMap = {}
      this.dadosLocal.forEach(it => {
        const nome = it.GRUPO || 'DIVERSOS'
        if (!gruposMap[nome]) gruposMap[nome] = 0
        gruposMap[nome] += (it.VL_LUCRO || 0)
      })
      const sorted = Object.entries(gruposMap).sort((a, b) => b[1] - a[1]).slice(0, 6)
      const labels = sorted.map(g => g[0])
      const values = sorted.map(g => g[1])
      this.charts.grupos = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom', labels: { boxWidth: 10, fontSize: 10 } }
        }
      })
    },
    renderProdutos() {
      if (!this.$refs.chartProdutos) return
      const ctx = this.$refs.chartProdutos.getContext('2d')
      if (this.charts.produtos) this.charts.produtos.destroy()
      const prodMap = {}
      this.dadosLocal.forEach(it => {
        const nome = it.PRODUTO || 'N/I'
        if (!prodMap[nome]) prodMap[nome] = 0
        prodMap[nome] += (it.VL_LUCRO || 0)
      })
      const sorted = Object.entries(prodMap).sort((a, b) => b[1] - a[1]).slice(0, 8)
      const labels = sorted.map(g => g[0].substring(0, 15))
      const values = sorted.map(g => g[1])
      this.charts.produtos = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels,
          datasets: [{
            label: 'Lucro',
            data: values,
            backgroundColor: '#3b82f6'
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ ticks: { beginAtZero: true, callback: value => this.formatBRL(value) } }]
          }
        }
      })
    },
    async obterEmpresaPreferencial() {
      try {
        const { data } = await this.$http.get('/api/protected/empresa/getPreferencial')
        return Array.isArray(data) ? data[0] : data
      } catch (e) {
        return { FANTASIA: 'CECWEB', RAZAO: 'CECWEB SISTEMA' }
      }
    },
    montarHtmlRelatorio(empresa) {
      const nomeUsuario = window.localStorage.getItem('nomeusuario') || 'SISTEMA'
      const dataEmissao = moment().format('DD/MM/YYYY HH:mm')
      
      // Captura os gráficos como imagem
      const imgEvolucao = this.$refs.chartEvolucao ? this.$refs.chartEvolucao.toDataURL('image/png') : ''
      const imgGrupos = this.$refs.chartGrupos ? this.$refs.chartGrupos.toDataURL('image/png') : ''
      const imgProdutos = this.$refs.chartProdutos ? this.$refs.chartProdutos.toDataURL('image/png') : ''

      let linhasTabela = ''
      this.resumoGrupos.forEach((g, i) => {
        linhasTabela += `<tr>
          <td>${i + 1}</td>
          <td>${g.GRUPO}</td>
          <td style="text-align:right;">${this.formatBRL(g.VL_BRUTO)}</td>
          <td style="text-align:right;">${this.formatBRL(g.VL_CUSTO)}</td>
          <td style="text-align:right; font-weight:700; color:#10b981;">${this.formatBRL(g.VL_LUCRO)}</td>
          <td style="text-align:center;">${Math.round((g.VL_LUCRO/g.VL_BRUTO)*100)}%</td>
        </tr>`
      })

      return `<!doctype html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Análise Gerencial de Lucratividade</title>
        <style>
          body { font-family: sans-serif; margin: 20px; color: #333; font-size: 11px; }
          .header { border-bottom: 2px solid #059669; padding-bottom: 10px; margin-bottom: 20px; display: flex; justify-content: space-between; }
          .report-title { text-align: center; font-size: 16px; font-weight: 700; text-transform: uppercase; margin: 15px 0; background: #ecfdf5; padding: 10px; border: 1px solid #059669; color: #065f46; }
          .kpi-row { display: flex; gap: 15px; margin-bottom: 20px; }
          .kpi-card { flex: 1; border: 1px solid #ddd; padding: 12px; border-radius: 6px; background: #fff; }
          .kpi-card small { display: block; font-size: 9px; text-transform: uppercase; color: #666; margin-bottom: 5px; font-weight: 700; }
          .kpi-card div { font-size: 15px; font-weight: 800; }
          .charts-row { display: flex; gap: 15px; margin-bottom: 20px; }
          .chart-box { flex: 1; border: 1px solid #eee; padding: 10px; text-align: center; background: #fafafa; }
          .chart-box h3 { font-size: 10px; margin-bottom: 8px; text-transform: uppercase; color: #444; border-bottom: 1px solid #eee; padding-bottom: 4px; }
          .chart-img { max-width: 100%; height: auto; max-height: 200px; }
          .data-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          .data-table th, .data-table td { border: 1px solid #ddd; padding: 6px; font-size: 10px; }
          .data-table th { background: #f2f2f2; text-align: left; text-transform: uppercase; }
          .footer { margin-top: 30px; border-top: 1px solid #ddd; padding-top: 10px; font-size: 9px; color: #999; display: flex; justify-content: space-between; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left"><h1>${empresa.FANTASIA || 'CECWEB'}</h1><span>${empresa.RAZAO || ''}</span></div>
          <div class="header-right"><div><strong>Emissor:</strong> ${nomeUsuario}</div><div><strong>Data:</strong> ${dataEmissao}</div></div>
        </div>
        <div class="report-title">Relatório de Análise Gerencial de Lucratividade</div>
        <div style="margin-bottom: 15px; font-size: 12px;">
          <strong>Período:</strong> ${this.mesExtenso} de ${this.filtro.ano}
        </div>
        <div class="kpi-row">
          <div class="kpi-card"><small>Faturamento Bruto</small><div>${this.formatBRL(this.totais.bruto)}</div></div>
          <div class="kpi-card"><small>Custo Mercadorias</small><div>${this.formatBRL(this.totais.custo)}</div></div>
          <div class="kpi-card"><small>Lucro Bruto Estimado</small><div style="color:#059669;">${this.formatBRL(this.totais.lucro)}</div></div>
        </div>
        <div class="charts-row">
          <div class="chart-box"><h3>Evolução do Lucro</h3><img src="${imgEvolucao}" class="chart-img"></div>
          <div class="chart-box"><h3>Lucro por Grupo</h3><img src="${imgGrupos}" class="chart-img"></div>
        </div>
        <div class="charts-row">
          <div class="chart-box"><h3>Top Produtos</h3><img src="${imgProdutos}" class="chart-img"></div>
        </div>
        <div style="font-weight:700; text-transform:uppercase; font-size:10px; margin-bottom:8px;">Desempenho por Grupo de Produtos</div>
        <table class="data-table">
          <thead>
            <tr>
              <th style="width:30px;">#</th>
              <th>Grupo de Produto</th>
              <th style="width:100px; text-align:right;">Faturamento</th>
              <th style="width:100px; text-align:right;">Custo</th>
              <th style="width:100px; text-align:right;">Lucro</th>
              <th style="width:60px; text-align:center;">Margem %</th>
            </tr>
          </thead>
          <tbody>${linhasTabela}</tbody>
        </table>
        <div class="footer"><div>CECWEB SISTEMA COMERCIAL</div><div>Página 1 de 1</div></div>
        <script>window.onload = function() { window.print(); }<\/script>
      </body>
      </html>`
    },
    async imprimirAnalise() {
      const janela = window.open('', '_blank')
      if (!janela) {
        this.$bvToast.toast('Navegador bloqueou a abertura do relatório.', { variant: 'warning' })
        return
      }
      try {
        const empresa = await this.obterEmpresaPreferencial()
        const html = this.montarHtmlRelatorio(empresa)
        janela.document.open()
        janela.document.write(html)
        janela.document.close()
      } catch (err) {
        console.error(err)
        janela.close()
        this.$bvToast.toast('Erro ao gerar relatório impresso', { variant: 'danger' })
      }
    }
  },
  beforeDestroy() {
    Object.values(this.charts).forEach(c => c && c.destroy())
  }
}
</script>

<style scoped>
.report-header-premium {
  background: linear-gradient(135deg, #059669 0%, #064e3b 100%);
  color: #fff;
  z-index: 10;
}
.report-header-premium__icon {
  width: 44px; height: 44px;
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
}
.report-header-premium__title { margin: 0; font-weight: 800; font-size: 1rem; text-transform: uppercase; }
.report-header-premium__sub { font-size: 0.8rem; opacity: 0.8; }

.bg-white-10 { background-color: rgba(255,255,255,0.15); }
.v-divider { width: 1px; height: 20px; background-color: rgba(255,255,255,0.3); }

.kpi-card-premium {
  background: #fff;
  padding: 1rem;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  border-left: 4px solid #e2e8f0;
}
.border-left-primary { border-left-color: #3b82f6; }
.border-left-danger { border-left-color: #ef4444; }
.border-left-success { border-left-color: #10b981; }

.kpi-card-premium small { text-transform: uppercase; font-weight: 700; color: #64748b; font-size: 0.6rem; display: block; margin-bottom: 0.4rem; }
.kpi-card-premium h4 { font-weight: 800; font-size: 1.1rem; margin: 0; }
.kpi-icon-bg { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; opacity: 0.8; }
.bg-primary-light { background: #dbeafe; color: #3b82f6; }
.bg-danger-light { background: #fee2e2; color: #ef4444; }
.bg-success-light { background: #d1fae5; color: #10b981; }

.chart-card { border-radius: 10px; }
.transition-all { transition: all 0.2s; }
.gap-2 { gap: 0.5rem; }

@media (max-width: 576px) {
  .report-header-premium__title { font-size: 0.9rem; }
  .filter-pills { width: 100%; justify-content: space-between; }
  .kpi-card-premium h4 { font-size: 1rem; }
}

@media print {
  body { overflow: visible !important; }
}
</style>



// WEBPACK FOOTER //
// src/views/dashboard/LucroDashboardModal.vue